import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subject, filter, mergeMap } from 'rxjs';

import { FormComponent } from '@ta/form-basic';
import { InputBase } from '@ta/form-model';
import { FilterHelper, NavigationComponent } from '@ta/menu';
import { ENotificationCode } from '@ta/notification';
import { TranslatePipe } from '@ta/translation';
import {
  BannerComponent,
  ButtonComponent,
  CheckboxComponent,
  LayoutFullPanelComponent,
  OverlineComponent,
  TextComponent,
} from '@ta/ui';
import { TA_AUTH_TOKEN } from '@ta/user';
import { isNonNullable } from '@ta/utils';

import { BaseComponent } from 'src/app/features/core/abstract/baseComponent';
import {
  RoleSelectionComponent,
  RoleSelectionState,
} from 'src/app/features/user/components/role-selection/role-selection.component';
import { CompanyService } from 'src/app/services/company/company.service';
import { CompanyMember } from 'src/app/services/company/dto/company-member';
import { CompanyFormService } from 'src/app/services/company/form/company-form.service';
import { UserProfileFormService } from 'src/app/services/user/form/user-profile-form.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-incomplete-profile-modal',
  standalone: true,
  imports: [
    BannerComponent,
    ButtonComponent,
    CheckboxComponent,
    FormComponent,
    LayoutFullPanelComponent,
    NavigationComponent,
    OverlineComponent,
    RoleSelectionComponent,
    TextComponent,
    TranslatePipe,
  ],
  templateUrl: './incomplete-profile.html',
  styleUrl: './incomplete-profile.scss',
})
export class IncompleteProfileModal extends BaseComponent implements OnInit {
  public open = input.required<boolean>();

  /** L'ouverture au fil du temps : la modale se reconstruit à chaque ouverture. */
  private readonly _open$ = toObservable(this.open);
  public closeEvent = output<void>();

  private _userFormService = inject(UserProfileFormService);
  private _authService = inject(TA_AUTH_TOKEN);
  private _companyService = inject(CompanyService);
  private _companyFormService = inject(CompanyFormService);

  public form = signal<InputBase<unknown>[]>([]);
  public hasRole = signal(false);
  public cguAccepted = signal(false);
  public askValidation$ = new Subject<null>();

  public activeTab = signal<'private' | 'pro'>('private');
  public readonly tabMenu = new FilterHelper([
    {
      defaultOpen: true,
      key: 'private',
      label: 'app.base.modal.incomplete_profile.tab_private',
      order: 1,
    },
    {
      defaultOpen: false,
      key: 'pro',
      label: 'app.base.modal.incomplete_profile.tab_pro',
      order: 2,
    },
  ]);
  public membership = signal<CompanyMember | null>(null);
  public companyForm = signal<InputBase<unknown>[]>(this._companyFormService.getCreationForm());
  public askCompanyValidation$ = new Subject<null>();

  public readonly cguUrl = `${environment.VITRINE_URL}/redirect?scope=cgu`;
  public canSubmit = computed(
    () => this.hasRole() && this.cguAccepted() && this._areInputsFilled(this.form()),
  );
  // Un représentant invité complète ses infos personnelles ; celui qui crée une société ne remplit que la sienne.
  public canSubmitPro = computed(() =>
    this.membership()?.id
      ? this.cguAccepted() && this._areInputsFilled(this.form())
      : this.cguAccepted() && this._areInputsFilled(this.companyForm()),
  );

  /** Ce qui retient encore la validation. */
  public submitHint = computed<string | null>(() => {
    const onPrivateTab = this.activeTab() === 'private';
    if (onPrivateTab ? this.canSubmit() : this.canSubmitPro()) {
      return null;
    }

    const fields = !onPrivateTab && !this.membership()?.id ? this.companyForm() : this.form();
    if (!this._areInputsFilled(fields)) {
      return 'app.base.modal.incomplete_profile.hint_fields';
    }
    if (onPrivateTab && !this.hasRole()) {
      return 'app.base.modal.incomplete_profile.hint_role';
    }
    if (!this.cguAccepted()) {
      return 'app.base.modal.incomplete_profile.hint_cgu';
    }
    return null;
  });

  ngOnInit() {
    // Le menu d'onglets est la source de vérité ; le signal en découle pour les conditions calculées.
    this._registerSubscription(
      this.tabMenu.refresh$.subscribe({
        next: (key: string) => {
          if (key === 'private' || key === 'pro') {
            this.activeTab.set(key);
          }
        },
      }),
    );

    this._registerSubscription(
      this._userService.userDetail.get$().subscribe({
        next: (user) => {
          this.hasRole.set(!!(user?.tenant?.id || user?.owner?.id));
          this.form.set(this._userFormService.getNameForm(user));
        },
      }),
    );

    // La demande de rattachement (companyMembership) n'est pas lue par `fetchDetails$` : elle vient du profil.
    this._registerSubscription(
      this.userProfile$.subscribe({
        next: (profile) => {
          const membership = profile?.companyMembership ?? null;
          this.membership.set(membership);
          if (membership?.id) {
            this.tabMenu.filter = 'pro';
          }
        },
      }),
    );

    this._registerSubscription(
      this._open$
        .pipe(
          filter(Boolean),
          mergeMap(() => this._userService.fetchDetails$()),
        )
        .subscribe(),
    );
  }

  public onSave(data: unknown) {
    if (this.activeTab() === 'private' && !this.hasRole()) {
      this._notificationService.addNotification(
        'app.base.modal.incomplete_profile.error_missing_role',
        ENotificationCode.error,
      );
      return;
    }
    this.requestState.asked();
    this._registerSubscription(
      this._userService.updateDetails$(this._userFormService.formatNameForm(data)).subscribe({
        complete: () => {
          this.requestState.completed();
          this.closeEvent.emit();
        },
        error: (err: HttpErrorResponse) => {
          this.requestState.onError(err.status, err.statusText);
          this._notificationService.addNotification(
            'app.notifications.messages.error',
            ENotificationCode.error,
          );
        },
      }),
    );
  }

  public onRoleChange(state: RoleSelectionState) {
    this.hasRole.set(state.isTenantActive || state.isOwnerActive);
  }

  public selectTab(tab: 'private' | 'pro') {
    this.tabMenu.filter = tab;
  }

  public onSubmitPro() {
    if (this.membership()?.id) {
      this.askValidation$.next(null);
      return;
    }
    this.askCompanyValidation$.next(null);
  }

  public onSaveCompany(data: unknown) {
    this.requestState.asked();

    this._registerSubscription(
      this._companyService
        .createCompany$(this._companyFormService.formatCreationForm(data))
        .subscribe({
          complete: () => {
            this.requestState.completed();
            // Le profil porte désormais le rattachement à la société.
            this._registerSubscription(this._userService.fetchUserProfile$().subscribe());
            this.closeEvent.emit();
          },
          error: (err: HttpErrorResponse) => {
            this.requestState.onError(err.status, err.statusText);
            this._notificationService.addNotification(
              'app.notifications.messages.error',
              ENotificationCode.error,
            );
          },
        }),
    );
  }

  public handleClose() {
    this._notificationService.addNotification(
      'app.base.modal.incomplete_profile.error_complete_required',
      ENotificationCode.error,
    );
  }

  public onSubmit() {
    this.askValidation$.next(null);
  }

  public logout() {
    this._authService.logout();
  }

  public openCgu() {
    window.open(this.cguUrl, '_blank', 'noopener,noreferrer');
  }

  private _areInputsFilled(inputs: InputBase<unknown>[]): boolean {
    return inputs.every((input) => {
      if (input.children.length > 0) {
        return this._areInputsFilled(input.children as InputBase<unknown>[]);
      }
      return isNonNullable(input.value) && input.value !== '';
    });
  }
}
